import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import BookShelf from "./components/BookShelf";

const STORAGE_KEY = "challenge_tracker_v1";

interface TrackerData {
  startDate: string;
  completedDays: number[];
}

function getStoredData(): TrackerData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as TrackerData) : null;
  } catch {
    return null;
  }
}

function saveData(data: TrackerData) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function hasJoinedChallenge(): boolean {
  return localStorage.getItem("challenge_joined") === "true";
}

function getDayIndex(startDate: string): number {
  const start = new Date(startDate);
  const now = new Date();
  start.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

function calcStreak(completed: number[], todayIndex: number): number {
  let streak = 0;
  for (let i = Math.min(todayIndex, 29); i >= 0; i--) {
    if (completed.includes(i)) streak++;
    else break;
  }
  return streak;
}

const identityMessages: Record<number, string> = {
  1: "Day one. The hardest one. You did it.",
  7: "One week. You are building something real.",
  14: "Halfway. You are not the same person who started.",
  21: "Three weeks. This is who you are now.",
  30: "Thirty days. You showed up every single one.",
};

function getMotivationalMessage(streak: number, completed: number): string {
  if (completed === 0) return "Your challenge starts the moment you check your first day.";
  if (streak === 0 && completed > 0) return "Miss a day? That's fine. Show up today — that's all that matters.";
  if (identityMessages[completed]) return identityMessages[completed];
  if (streak >= 7) return `${streak} days in a row. This is a pattern. This is identity.`;
  if (streak >= 3) return `${streak} consecutive days. You are becoming the person who shows up.`;
  return "Every day you check in, you're proving something to yourself.";
}

export default function Tracker() {
  const [data, setData] = useState<TrackerData | null>(null);
  const [joined, setJoined] = useState<boolean>(false);
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    const isJoined = hasJoinedChallenge();
    setJoined(isJoined);
    if (isJoined) {
      const stored = getStoredData();
      if (stored) {
        setData(stored);
      }
    }
  }, []);

  const startChallenge = useCallback(() => {
    const fresh: TrackerData = {
      startDate: new Date().toISOString().split("T")[0],
      completedDays: [],
    };
    saveData(fresh);
    setData(fresh);
    setShowReset(false);
  }, []);

  const toggleDay = useCallback(
    (dayIndex: number) => {
      if (!data) return;
      const todayIndex = getDayIndex(data.startDate);
      if (dayIndex > todayIndex || dayIndex > 29) return;

      const updated = data.completedDays.includes(dayIndex)
        ? data.completedDays.filter((d) => d !== dayIndex)
        : [...data.completedDays, dayIndex];

      const next = { ...data, completedDays: updated };
      saveData(next);
      setData(next);
    },
    [data]
  );

  if (!joined) {
    return (
      <main className="bg-[#F4F3F0]">
        <Navbar />
        <section className="min-h-screen flex items-center justify-center px-8">
          <div className="max-w-lg w-full text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-8 bg-[#3A5544]/10 rounded-full">
              <i className="ri-lock-line text-3xl text-[#3A5544]" />
            </div>
            <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-widest mb-4 block">Step one first</span>
            <h1 className="font-serif text-4xl lg:text-5xl text-[#1A1A1A] leading-[1.15] mb-5">
              The tracker is waiting<br />for you to commit.
            </h1>
            <p className="text-[#666] text-base leading-relaxed mb-10">
              Join the 30-Day Challenge first — it only takes a minute. Once you're in, your personal tracker unlocks and your 30 days begin.
            </p>
            <Link
              to="/challenge"
              className="inline-flex items-center gap-2 bg-[#3A5544] text-white px-10 py-4 rounded font-medium hover:bg-[#2D4238] transition-colors duration-200 cursor-pointer whitespace-nowrap text-sm"
            >
              Join the challenge <i className="ri-arrow-right-line" />
            </Link>
            <p className="text-[#AAA] text-xs mt-4">Free. No account. Just your commitment.</p>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  if (!data) {
    return (
      <main className="bg-[#F4F3F0]">
        <Navbar />
        <section className="min-h-screen flex items-center justify-center px-8">
          <div className="max-w-lg w-full text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-8 bg-[#3A5544]/10 rounded-full">
              <i className="ri-book-open-line text-3xl text-[#3A5544]" />
            </div>
            <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-widest mb-4 block">30-Day Challenge</span>
            <h1 className="font-serif text-4xl lg:text-5xl text-[#1A1A1A] leading-[1.15] mb-5">
              Ready to become<br />a daily reader?
            </h1>
            <p className="text-[#666] text-base leading-relaxed mb-10">
              This tracker lives in your browser. No account needed. Just open it each day, mark your 10 pages done, and watch who you become over 30 days.
            </p>
            <button
              onClick={startChallenge}
              className="bg-[#3A5544] text-white px-10 py-4 rounded font-medium hover:bg-[#2D4238] transition-colors duration-200 cursor-pointer whitespace-nowrap text-sm"
            >
              Start my 30 days — today
            </button>
            <p className="text-[#AAA] text-xs mt-4">Your progress saves automatically to this browser.</p>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  const todayIndex = getDayIndex(data.startDate);
  const streak = calcStreak(data.completedDays, todayIndex);
  const completed = data.completedDays.length;
  const remaining = Math.max(0, 30 - completed);
  const progressPct = Math.round((completed / 30) * 100);
  const isChallengeOver = todayIndex >= 29;
  const message = getMotivationalMessage(streak, completed);

  const startDateFormatted = new Date(data.startDate).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <main className="bg-[#F4F3F0]">
      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-12 px-8 lg:px-24 bg-[#F4F3F0]">
        <div className="max-w-4xl mx-auto">
          <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-widest mb-4 block">30-Day Challenge</span>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <h1 className="font-serif text-4xl lg:text-5xl text-[#1A1A1A] leading-[1.15] mb-3">
                Your daily tracker.
              </h1>
              <p className="text-[#888] text-sm">Started {startDateFormatted}</p>
            </div>
            <button
              onClick={() => setShowReset(!showReset)}
              className="text-[#AAA] text-xs hover:text-[#666] transition-colors cursor-pointer whitespace-nowrap self-start lg:self-end"
            >
              Start over
            </button>
          </div>
        </div>
      </section>

      {/* Reset confirmation */}
      {showReset && (
        <div className="px-8 lg:px-24 pb-6">
          <div className="max-w-4xl mx-auto bg-white border border-[#E0DDD8] rounded-lg p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <p className="text-[#555] text-sm">This will erase your current progress and start fresh from today.</p>
            <div className="flex items-center gap-3 flex-shrink-0">
              <button onClick={() => setShowReset(false)} className="text-[#888] text-sm cursor-pointer hover:text-[#333] whitespace-nowrap">Cancel</button>
              <button onClick={startChallenge} className="bg-[#1A1A1A] text-white text-sm px-4 py-2 rounded cursor-pointer hover:bg-[#333] transition-colors whitespace-nowrap">Yes, reset</button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Row */}
      <section className="px-8 lg:px-24 pb-10">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-4">
          {[
            { value: `${streak}`, label: "Day streak", icon: "ri-fire-line" },
            { value: `${completed}`, label: "Days complete", icon: "ri-checkbox-circle-line" },
            { value: `${remaining}`, label: "Days remaining", icon: "ri-calendar-line" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-lg p-6 text-center">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                <i className={`${s.icon} text-xl text-[#3A5544]`} />
              </div>
              <p className="font-serif text-3xl text-[#1A1A1A] font-bold mb-1">{s.value}</p>
              <p className="text-[#888] text-xs uppercase tracking-widest">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Progress bar + message */}
      <section className="px-8 lg:px-24 pb-10">
        <div className="max-w-4xl mx-auto bg-[#3A5544] rounded-lg p-8">
          <div className="flex items-start justify-between mb-5 gap-4">
            <p className="font-serif text-xl text-white leading-snug max-w-sm">{message}</p>
            <span className="text-white/60 text-2xl font-serif flex-shrink-0">{progressPct}%</span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </section>

      {/* 30-Day Grid */}
      <section className="px-8 lg:px-24 pb-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#888] text-xs uppercase tracking-widest mb-6 font-semibold">
            Tap a day to mark it complete — you can only check today or past days
          </p>
          <div className="grid grid-cols-6 sm:grid-cols-10 gap-3">
            {Array.from({ length: 30 }, (_, i) => {
              const isCompleted = data.completedDays.includes(i);
              const isToday = i === todayIndex;
              const isFuture = i > todayIndex;
              const dayNum = i + 1;

              return (
                <button
                  key={i}
                  onClick={() => toggleDay(i)}
                  disabled={isFuture}
                  title={isFuture ? "Not yet" : isCompleted ? `Day ${dayNum} — done` : `Day ${dayNum} — mark as read`}
                  className={`
                    aspect-square rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all duration-200 text-xs font-medium
                    ${isCompleted ? "bg-[#3A5544] text-white cursor-pointer" : ""}
                    ${isToday && !isCompleted ? "bg-white border-2 border-[#3A5544] text-[#3A5544] cursor-pointer ring-2 ring-[#3A5544]/20" : ""}
                    ${!isCompleted && !isToday && !isFuture ? "bg-white text-[#555] hover:bg-[#ECEAE6] cursor-pointer" : ""}
                    ${isFuture ? "bg-[#ECEAE6] text-[#CCC] cursor-default" : ""}
                  `}
                >
                  {isCompleted ? (
                    <i className="ri-check-line text-sm" />
                  ) : (
                    <span>{dayNum}</span>
                  )}
                  {isToday && <span className="text-[9px] uppercase tracking-widest opacity-70">Today</span>}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-6 mt-8">
            {[
              { color: "bg-[#3A5544]", label: "Completed" },
              { color: "bg-white border-2 border-[#3A5544]", label: "Today" },
              { color: "bg-white border border-[#E0DDD8]", label: "Past — not logged" },
              { color: "bg-[#ECEAE6]", label: "Upcoming" },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded ${l.color} flex-shrink-0`} />
                <span className="text-[#888] text-xs">{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Tracker */}
      <BookShelf />

      {/* Completion Banner */}
      {isChallengeOver && completed === 30 && (
        <section className="px-8 lg:px-24 pb-16">
          <div className="max-w-4xl mx-auto bg-[#111111] rounded-lg p-12 text-center">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-6">
              <i className="ri-trophy-line text-4xl text-[#6B9E8A]" />
            </div>
            <h2 className="font-serif text-4xl text-white mb-4">You did it.</h2>
            <p className="text-white/60 text-base leading-relaxed max-w-md mx-auto mb-8">
              30 days. Every single one. You are not someone who is trying to build a reading habit — you are a reader. That's not the challenge talking. That's the proof.
            </p>
            <button
              onClick={startChallenge}
              className="bg-[#3A5544] text-white px-8 py-3.5 rounded text-sm font-medium hover:bg-[#2D4238] transition-colors cursor-pointer whitespace-nowrap"
            >
              Start the next 30 days
            </button>
          </div>
        </section>
      )}

      {/* Bottom tip */}
      <section className="px-8 lg:px-24 pb-20">
        <div className="max-w-4xl mx-auto bg-white rounded-lg p-7 flex items-start gap-5">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#F4F3F0] rounded">
            <i className="ri-bookmark-line text-[#3A5544]" />
          </div>
          <div>
            <p className="text-[#1A1A1A] text-sm font-medium mb-1">Bookmark this page</p>
            <p className="text-[#777] text-sm leading-relaxed">
              Your progress is saved automatically in this browser. Bookmark this page so you can return each day to log your reading — no login, no app, no friction.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
